import { ChangeEvent, useEffect, useState } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import * as yup from "yup";

import { EntityRole } from "../../../api/courses-users.generated";
import { FeatureKey } from "../../../api/me.generated";
import { useGetBackOfficeListRolesQuery } from "../../../api/roles.generated";
import { useUpdateUserByIdMutation } from "../../../api/user.generated";
import { EntityUser } from "../../../api/users.generated";
import { UserFormValues } from "../../../enums/form";
import { Optional } from "../../../enums/types";
import { useUpload, useUploadMedia } from "../../../hooks";
import { useGetImage } from "../../../hooks/useGetImage";
import { emailRegex } from "../../../utils/isEventTriggeredInElement";
import { handleValidationErrors } from "../../../utils/validationErrorHandler";
import { PageLoader } from "../../loader/PageLoader";
import { UserCoursesCard } from "../molecules/UserCoursesCard";
import { UserDetailForm } from "../molecules/UserDetailForm";
import { UserDevicesCard } from "../molecules/UserDevicesCard";
import { UserGoalsCard } from "../molecules/UserGoalsCard";
import { UserPaymentsCard } from "../molecules/UserPaymentsCard";
import { UserSubscriptionCard } from "../molecules/UserSubscriptionCard";

type Props = {
  isSuccess: boolean;
  isError: boolean;
  currentData?: EntityUser;
  isFetching: boolean;
  id: string;
  onUserStatusChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isUserDeactivated: boolean;
};

export function UsersDetailPageContent({
  isError,
  isFetching,
  isSuccess,
  currentData,
  id,
  onUserStatusChange,
  isUserDeactivated,
}: Props) {
  const { t } = useTranslation();
  const [userUpdate] = useUpdateUserByIdMutation();
  const response = useGetBackOfficeListRolesQuery();

  const { addFileToFormData } = useUpload();
  const [isDirtyFromImage, setIsDirtyFromImage] = useState<boolean>(false);
  const allFeatureKeys: FeatureKey[] = ["chat", "credit_purchase"];
  const [blockedFeatures, setBlockedFeatures] = useState<string[]>(
    currentData?.blockedFeatures || []
  );
  const [savedBlockedFeatures, setSavedBlockedFeatures] = useState<string[]>(
    currentData?.blockedFeatures || []
  );

  const FormSchema = yup.object().shape({
    name: yup.string().required(t("form.name.required")),
    role: yup.string(),
    email: yup
      .string()
      .matches(emailRegex, t("form.email.invalid"))
      .required(t("form.email.required")),
  });
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, isValid },
    getValues,
    reset,
    setError,
  } = useForm<UserFormValues>({
    defaultValues: {
      name: currentData?.name || "",
      email: currentData?.email || "",
      role: currentData?.role || "",
    },
    mode: "all",
    resolver: yupResolver(FormSchema),
  });
  const [userImageId, setUserImageId] = useState<string>(
    currentData?.image?.id || ""
  );
  const [imageFormData, setImageFormData] = useState<Optional<FormData>>(null);
  const { uploadedMediaId, isMediaUploading } = useUploadMedia(imageFormData);
  const { downloadedImage, isImageDownloading } = useGetImage(
    uploadedMediaId || userImageId
  );

  useEffect(
    function resetFormDataAfterFetch() {
      if (!isFetching && !!currentData) {
        reset({
          name: currentData?.name,
          email: currentData?.email,
          role: currentData?.role,
        });
        setBlockedFeatures(currentData?.blockedFeatures || []);
        setSavedBlockedFeatures(currentData?.blockedFeatures || []);
      }
    },
    [currentData]
  );

  useEffect(
    function setUserImageIdAfterUserDataFetch() {
      if (!isFetching && !isError && isSuccess) {
        currentData?.image?.id && setUserImageId(currentData?.image?.id);
        setIsDirtyFromImage(false);
      }
    },
    [currentData, isFetching, isError, isSuccess]
  );

  const onSubmit = () => {
    const { name, role, email } = getValues();

    userUpdate({
      id: currentData?.id || "",
      userUpdateRequest: {
        name,
        email,
        role,
        blockedFeatures,
        sendNotifications: currentData?.sendNotifications || false,
        timezone: currentData?.timezone || "Europe/Zurich",
        profileImageID: uploadedMediaId || currentData?.image?.id,
      },
    })
      .unwrap()
      .then(() => {
        reset({
          name,
          role,
          email,
        });
        setIsDirtyFromImage(false);
        setSavedBlockedFeatures([...blockedFeatures]);
        toast.success(t("app.user.edit.success"));
      })
      .catch((error) => {
        const { hasFieldErrors, errorMessage } = handleValidationErrors(
          error,
          setError
        );
        if (!hasFieldErrors) {
          toast.warning(errorMessage || t("app.user.edit.failed"));
        }
      });
  };

  const onChangePicture = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      setImageFormData(addFileToFormData(e.target.files[0]));
      setIsDirtyFromImage(true);
    }
  };

  const onBlockedFeaturesChange = (feature: string) => {
    setBlockedFeatures((prev) =>
      prev.includes(feature)
        ? prev.filter((f) => f !== feature)
        : [...prev, feature]
    );
  };

  const isBlockedFeaturesDirty =
    blockedFeatures.length !== savedBlockedFeatures.length ||
    blockedFeatures.some((f) => !savedBlockedFeatures.includes(f));

  const isFormDirty = isDirty || isDirtyFromImage || isBlockedFeaturesDirty;
  const isImageLoading = isImageDownloading || isMediaUploading;
  const isSubmitButtonDisabled =
    !isFormDirty || isImageLoading || isFetching || !isValid;
  const defaultRoleValue = {
    value: currentData?.role || "",
    label: currentData?.role || "",
  };

  const status = isUserDeactivated
    ? t("app.status.user.deactivated")
    : t("app.status.user.active");

  const mapRoleToLabel: {
    [key in EntityRole]: string;
  } = {
    "rs-admin": t("app.role.admin"),
    "rs-user": t("app.role.user"),
    "rs-system-admin": t("app.role.system-admin"),
    "rs-tester": t("app.role.tester"),
  };
  const roleOptions = response.data?.map((role) => {
    const label = mapRoleToLabel[role as EntityRole];
    return {
      value: role,
      label,
    };
  });

  return isFetching ? (
    <PageLoader />
  ) : (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 p-2 w-full min-w-full">
      <div className="flex flex-col gap-4">
        <UserDetailForm
          control={control}
          errors={errors}
          onChangePicture={onChangePicture}
          handleSubmit={handleSubmit(onSubmit)}
          userImage={downloadedImage || ""}
          badgeLabel={currentData?.userOrigin || ""}
          isImageLoading={isImageLoading}
          defaultRoleValue={defaultRoleValue}
          roleOptions={roleOptions || []}
          isSubmitButtonDisabled={isSubmitButtonDisabled}
          onSumbit={onSubmit}
          status={status}
          isUserDeactivated={isUserDeactivated}
          onUserDeactivation={onUserStatusChange}
          blockedFeatures={blockedFeatures}
          onBlockedFeaturesChange={onBlockedFeaturesChange}
          allFeatureKeys={allFeatureKeys}
        />
        <UserSubscriptionCard subscription={currentData?.subscription} />
        <UserCoursesCard id={id || ""} />
        <UserDevicesCard id={id || ""} />
      </div>
      <div className="flex flex-col gap-4">
        <UserGoalsCard id={id || ""} />
        <UserPaymentsCard id={id || ""} />
      </div>
    </div>
  );
}
